import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const CorporateDashboardScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [account, setAccount] = useState(null);
  const [leases, setLeases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leases');

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: corpData } = await supabase?.from('corporate_accounts')?.select('*')?.eq('admin_user_id', user?.id)?.maybeSingle();
      if (corpData) {
        setAccount(corpData);
        const [leasesRes, invoicesRes] = await Promise.all([
          supabase?.from('corporate_leases')?.select('*')?.eq('corporate_id', corpData?.id)?.order('created_at', { ascending: false }),
          supabase?.from('rental_invoices')?.select('*')?.eq('corporate_id', corpData?.id)?.order('created_at', { ascending: false }),
        ]);
        setLeases(leasesRes?.data || []);
        setInvoices(invoicesRes?.data || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const leaseStatusColors = { draft: '#94a3b8', sent: '#60a5fa', accepted: '#34d399', active: '#ffd700', ended: '#a78bfa' };
  const invoiceStatusColors = { pending: '#fbbf24', paid: '#34d399', overdue: '#f87171', cancelled: '#94a3b8' };
  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const totalMonthly = leases?.filter(l => l?.lease_status === 'active')?.reduce((sum, l) => sum + (l?.monthly_price || 0), 0);
  const pendingInvoices = invoices?.filter(i => i?.inv_status === 'pending')?.length;

  if (!user) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a1628' }}>
      <div className="text-center">
        <div className="text-6xl mb-4">🔐</div>
        <h3 className="text-white font-bold text-xl mb-4">Sign in to access Corporate Dashboard</h3>
        <button onClick={() => navigate('/customer-login-screen')} className="btn-primary">Sign In</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/corporate-leasing')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
              <Icon name="ArrowLeft" size={20} className="text-white" />
            </button>
            <div>
              <h1 className="text-white font-bold text-lg">Corporate Dashboard</h1>
              {account && <p className="text-white/40 text-xs">{account?.company_name}</p>}
            </div>
          </div>
          <button onClick={() => navigate('/lease-request')} className="btn-primary text-sm py-2 px-4">
            New Request
          </button>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4]?.map(i => <div key={i} className="h-24 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />)}
          </div>
        ) : !account ? (
          <div className="text-center py-24">
            <div className="text-6xl mb-4">🏢</div>
            <h3 className="text-white font-bold text-xl mb-2">No corporate account found</h3>
            <p className="text-white/40 text-sm mb-6">Submit a lease request to get started</p>
            <button onClick={() => navigate('/lease-request')} className="btn-primary">Request a Lease</button>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Active Leases', value: leases?.filter(l => l?.lease_status === 'active')?.length, icon: 'Car', color: '#ffd700' },
                { label: 'Monthly Cost', value: `KES ${totalMonthly?.toLocaleString()}`, icon: 'DollarSign', color: '#34d399' },
                { label: 'Pending Invoices', value: pendingInvoices, icon: 'FileText', color: '#f87171' },
                { label: 'Total Leases', value: leases?.length, icon: 'BarChart3', color: '#60a5fa' },
              ]?.map(stat => (
                <div key={stat?.label} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name={stat?.icon} size={20} className="mb-3" style={{ color: stat?.color }} />
                  <div className="text-white font-black text-xl">{stat?.value}</div>
                  <div className="text-white/40 text-xs mt-0.5">{stat?.label}</div>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              {[{ key: 'leases', label: 'Active Leases' }, { key: 'invoices', label: 'Invoices' }]?.map(tab => (
                <button key={tab?.key} onClick={() => setActiveTab(tab?.key)}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
                  style={{ background: activeTab === tab?.key ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.06)', color: activeTab === tab?.key ? '#ffd700' : 'rgba(255,255,255,0.5)', border: `1px solid ${activeTab === tab?.key ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* Leases Tab */}
            {activeTab === 'leases' && (
              <div className="space-y-4">
                {leases?.length === 0 ? (
                  <div className="text-center py-16 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="text-5xl mb-3">🚗</div>
                    <p className="text-white/40 text-sm">No leases yet. Submit a request to get started.</p>
                  </div>
                ) : leases?.map(lease => (
                  <div key={lease?.id} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-white font-bold text-base">#{lease?.lease_number}</h3>
                        <p className="text-white/40 text-xs mt-0.5">{formatDate(lease?.start_at)} → {formatDate(lease?.end_at)}</p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold capitalize" style={{ background: `${leaseStatusColors?.[lease?.lease_status]}20`, color: leaseStatusColors?.[lease?.lease_status] }}>
                        {lease?.lease_status}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div><div className="text-white/40 text-xs">Monthly</div><div className="text-[#ffd700] font-bold">KES {lease?.monthly_price?.toLocaleString()}</div></div>
                      <div><div className="text-white/40 text-xs">Vehicles</div><div className="text-white font-bold">{Array.isArray(lease?.vehicles) ? lease?.vehicles?.length : '—'}</div></div>
                      <div><div className="text-white/40 text-xs">Deposit Terms</div><div className="text-white font-semibold text-xs">{lease?.deposit_terms || 'N/A'}</div></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div className="space-y-3">
                {invoices?.length === 0 ? (
                  <div className="text-center py-16 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="text-5xl mb-3">📄</div>
                    <p className="text-white/40 text-sm">No invoices yet</p>
                  </div>
                ) : invoices?.map(invoice => (
                  <div key={invoice?.id} className="rounded-2xl p-4 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div>
                      <div className="text-white font-bold text-sm">#{invoice?.invoice_number}</div>
                      <div className="text-white/40 text-xs mt-0.5">Due: {formatDate(invoice?.due_date)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[#ffd700] font-black text-lg">KES {invoice?.amount?.toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold capitalize" style={{ background: `${invoiceStatusColors?.[invoice?.inv_status]}20`, color: invoiceStatusColors?.[invoice?.inv_status] }}>
                        {invoice?.inv_status}
                      </span>
                      {invoice?.inv_status === 'pending' && (
                        <button className="btn-primary text-xs py-1.5 px-3">Pay Now</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CorporateDashboardScreen;
